import { matchPath } from 'react-router-dom';
import { adminHelp } from './content/admin';
import { peopleHelp } from './content/people';
import { workspaceHelp } from './content/workspace';
import { generalHelp } from './general';
import type { HelpTopic } from './types';

export type { HelpField, HelpSection, HelpTopic } from './types';
export { generalHelp };

/** Every screen's guide. */
export const HELP_TOPICS: HelpTopic[] = [...workspaceHelp, ...peopleHelp, ...adminHelp];

const patterns = (t: HelpTopic) => (Array.isArray(t.path) ? t.path : [t.path]);

/** Static segments count more than `:params`, so `/tasks/reassignments` beats `/tasks/:id`. */
function score(pattern: string): number {
  return pattern
    .split('/')
    .filter(Boolean)
    .reduce((n, seg) => n + (seg.startsWith(':') ? 1 : seg === '*' ? 0 : 3), 0);
}

/** The guide for an address: the most specific matching pattern wins. */
export function topicFor(pathname: string): HelpTopic | null {
  let best: { topic: HelpTopic; score: number } | null = null;
  for (const topic of HELP_TOPICS) {
    for (const p of patterns(topic)) {
      if (!matchPath({ path: p, end: true }, pathname)) continue;
      const s = score(p);
      if (!best || s > best.score) best = { topic, score: s };
    }
  }
  return best?.topic ?? null;
}

/** First pattern, with `:params` cut off: the address to open a guide's screen from the list. */
export function topicHome(topic: HelpTopic): string {
  const p = patterns(topic)[0];
  const cut = p.indexOf('/:');
  return cut >= 0 ? p.slice(0, cut) || '/' : p;
}

/** All words of a topic, lower-cased, for the search box. */
export function topicText(topic: HelpTopic): string {
  return [
    topic.title,
    topic.summary,
    ...topic.sections.flatMap((s) => [
      s.title,
      s.body ?? '',
      ...(s.steps ?? []),
      ...(s.bullets ?? []),
      ...(s.fields ?? []).flatMap((f) => [f.name, f.help]),
    ]),
  ]
    .join(' ')
    .toLowerCase();
}
